/**
 * Cell renderer for profile cross-section preview
 * Renders an SVG path as a stroked line (no fill)
 */

// =============================================================================
// HELPERS
// =============================================================================

type PathBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type PathCursor = {
  x: number;
  y: number;
};

export interface PathCommand {
  type: string;
  coords: number[];
  point?: [number, number];
}

function parseCommandNumbers(cmd: string): number[] {
  return cmd
    .slice(1)
    .trim()
    .split(/[\s,]+/)
    .map(Number)
    .filter((n) => !Number.isNaN(n));
}

function pointCommand(
  type: string,
  coords: number[],
  cursor: PathCursor,
): PathCommand {
  return { type, coords, point: [cursor.x, cursor.y] };
}

function parsePathCommand(cmd: string, cursor: PathCursor): PathCommand | null {
  const type = cmd.charAt(0).toUpperCase();
  if (!type) return null;

  const coords = parseCommandNumbers(cmd);

  switch (type) {
    case "M":
    case "L": {
      const [x, y] = coords;
      if (x === undefined || y === undefined) return null;
      cursor.x = x;
      cursor.y = y;
      return pointCommand(type, coords, cursor);
    }
    case "H": {
      const [x] = coords;
      if (x === undefined) return null;
      cursor.x = x;
      return pointCommand(type, coords, cursor);
    }
    case "V": {
      const [y] = coords;
      if (y === undefined) return null;
      cursor.y = y;
      return pointCommand(type, coords, cursor);
    }
    case "Z":
      return { type: "Z", coords: [] };
    default:
      return null;
  }
}

function pathCommandStrings(d: string): string[] {
  return d
    .replaceAll(/[\s,]+/g, " ")
    .split(/(?=[MmLlHhVvCcSsQqTtAaZz])/)
    .filter((c) => c.trim().length > 0);
}

/**
 * Parse SVG path commands to estimate bounding box
 * This works in Node.js environments where document API is not available
 */
function parseSvgPathBounds(d: string): PathBounds {
  const points = parseSvgPathCommands(d)
    .filter(pathCommandHasPoint)
    .map((command) => command.point);

  if (points.length === 0) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }

  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Measure SVG path dimensions
 * Uses DOM API in browser, falls back to path parsing in Node.js
 */
export function measureSvgPath(d: string): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  if (typeof document !== "undefined") {
    // Browser environment: use DOM API
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.style.position = "absolute";
    svg.style.visibility = "hidden";
    svg.style.width = "0px";
    svg.style.height = "0px";
    document.body.appendChild(svg);

    const path = document.createElementNS(ns, "path");
    path.setAttribute("d", d);
    svg.appendChild(path);

    try {
      const bbox = path.getBBox();
      return bbox;
    } catch (e) {
      console.error("Failed to measure SVG path:", e);
      return parseSvgPathBounds(d);
    } finally {
      svg.remove();
    }
  }

  // Node.js environment: parse path manually
  return parseSvgPathBounds(d);
}

/**
 * Parse SVG path to extract points and commands
 */
function pathCommandHasPoint(
  c: PathCommand,
): c is PathCommand & { point: [number, number] } {
  return c.point != null;
}

export function parseSvgPathCommands(d: string): PathCommand[] {
  const commands: PathCommand[] = [];
  const cursor = { x: 0, y: 0 };

  for (const cmd of pathCommandStrings(d)) {
    const parsed = parsePathCommand(cmd, cursor);
    if (parsed) commands.push(parsed);
  }

  return commands;
}

/**
 * Identify the zigzag line and box from the path
 * Returns: { zigzagPoints: Array<[number, number]>, boxPoints: Array<[number, number]>, bbox }
 */
export function identifyZigzagAndBox(
  commands: PathCommand[],
  bbox: { x: number; y: number; width: number; height: number },
): {
  zigzagPoints: Array<[number, number]>;
  boxPoints: Array<[number, number]>;
} {
  const points = commands.filter(pathCommandHasPoint).map((c) => c.point);

  if (points.length < 4) {
    // Not enough points, return all as zigzag
    return { zigzagPoints: points, boxPoints: [] };
  }

  // Try to identify the box: look for the last 4-5 points that form a rectangle
  // The box typically has points at the corners: bottom-left, bottom-right, top-right, top-left
  const { x, y, width, height } = bbox;
  const tolerance = 0.1; // Small tolerance for floating point comparison

  // Check if the last points form a rectangle matching the bbox
  let boxStartIndex = points.length;
  for (let i = Math.max(0, points.length - 5); i < points.length; i++) {
    const remainingPoints = points.slice(i);
    if (remainingPoints.length >= 4) {
      // Check if these points form a rectangle matching the bbox
      const hasBottomLeft = remainingPoints.some(
        (p) => Math.abs(p[0] - x) < tolerance && Math.abs(p[1] - y) < tolerance,
      );
      const hasBottomRight = remainingPoints.some(
        (p) =>
          Math.abs(p[0] - (x + width)) < tolerance &&
          Math.abs(p[1] - y) < tolerance,
      );
      const hasTopLeft = remainingPoints.some(
        (p) =>
          Math.abs(p[0] - x) < tolerance &&
          Math.abs(p[1] - (y + height)) < tolerance,
      );
      const hasTopRight = remainingPoints.some(
        (p) =>
          Math.abs(p[0] - (x + width)) < tolerance &&
          Math.abs(p[1] - (y + height)) < tolerance,
      );

      if (hasBottomLeft && hasBottomRight && (hasTopLeft || hasTopRight)) {
        boxStartIndex = i;
        break;
      }
    }
  }

  // If we found a box, split the points
  if (boxStartIndex < points.length) {
    return {
      zigzagPoints: points.slice(0, boxStartIndex),
      boxPoints: points.slice(boxStartIndex),
    };
  }

  // Fallback: assume last 4 points are the box
  if (points.length >= 4) {
    return {
      zigzagPoints: points.slice(0, -4),
      boxPoints: points.slice(-4),
    };
  }

  return { zigzagPoints: points, boxPoints: [] };
}

/**
 * Create a fill path from the zigzag line to the box edge
 */
export function createZigzagFillPath(
  zigzagPoints: Array<[number, number]>,
  _boxPoints: Array<[number, number]>,
  bbox: { x: number; y: number; width: number; height: number },
  fillUpper: boolean,
): string {
  if (zigzagPoints.length === 0) {
    return "";
  }

  const { x, y, height } = bbox;
  const firstZigzagPoint = zigzagPoints[0];
  const lastZigzagPoint = zigzagPoints.at(-1);
  if (!firstZigzagPoint || !lastZigzagPoint) {
    return "";
  }

  // Build the path: start from the box edge, follow zigzag, then close back to box
  let path = "";

  if (fillUpper) {
    // Fill upper half: start from top-left of box, go to first zigzag point
    path += `M ${x} ${y + height} `;
    path += `L ${firstZigzagPoint[0]} ${firstZigzagPoint[1]} `;
    // Follow the zigzag line
    for (let i = 1; i < zigzagPoints.length; i++) {
      const zp = zigzagPoints[i];
      if (!zp) continue;
      path += `L ${zp[0]} ${zp[1]} `;
    }
    // Go to last zigzag point's x position on the top edge
    path += `L ${lastZigzagPoint[0]} ${y + height} `;
    // Close back to start
    path += `L ${x} ${y + height} Z`;
  } else {
    // Fill lower half: start from bottom-left of box, go to first zigzag point
    path += `M ${x} ${y} `;
    path += `L ${firstZigzagPoint[0]} ${firstZigzagPoint[1]} `;
    // Follow the zigzag line
    for (let i = 1; i < zigzagPoints.length; i++) {
      const zp = zigzagPoints[i];
      if (!zp) continue;
      path += `L ${zp[0]} ${zp[1]} `;
    }
    // Go to last zigzag point's x position on the bottom edge
    path += `L ${lastZigzagPoint[0]} ${y} `;
    // Close back to start
    path += `L ${x} ${y} Z`;
  }

  return path;
}

/**
 * Render a profile cross-section preview in a table cell
 */
export type RenderProfilePreviewOptions = {
  svgPath: string;
  cellX: number;
  cellY: number;
  cellWidth: number;
  cellHeight: number;
  page: import("pdf-lib").PDFPage;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
  position?: string;
};

export function renderProfilePreview({
  svgPath,
  cellX,
  cellY,
  cellWidth,
  cellHeight,
  page,
  rgb,
  position,
}: RenderProfilePreviewOptions): void {
  if (!svgPath || svgPath.trim().length === 0) {
    return;
  }

  const bbox = measureSvgPath(svgPath);
  if (bbox.width <= 0 || bbox.height <= 0) {
    return;
  }

  // Calculate scale to fit in cell (with padding)
  const padding = 2; // points
  const maxW = cellWidth - padding * 2;
  const maxH = cellHeight - padding * 2;

  const scaleW = maxW / bbox.width;
  const scaleH = maxH / bbox.height;
  const scale = Math.min(scaleW, scaleH);

  // Center in cell
  const cellCenterX = cellX + cellWidth / 2;
  const cellCenterY = cellY - cellHeight / 2;

  // Calculate position to center the bbox
  const pathX = cellCenterX - (bbox.x + bbox.width / 2) * scale;
  const pathY = cellCenterY - (bbox.y + bbox.height / 2) * scale;

  // Determine which half to fill based on position
  // "Oben" = upper profile = fill upper half
  // "Unten" = lower profile = fill lower half
  const fillUpper = position === "Oben";

  // Draw filled half if position is specified
  if (position === "Oben" || position === "Unten") {
    // Parse the SVG path to identify zigzag and box
    const commands = parseSvgPathCommands(svgPath);
    const { zigzagPoints, boxPoints } = identifyZigzagAndBox(commands, bbox);

    if (zigzagPoints.length > 0) {
      // Create fill path from zigzag to box edge
      const fillPath = createZigzagFillPath(
        zigzagPoints,
        boxPoints,
        bbox,
        fillUpper,
      );

      if (fillPath) {
        page.drawSvgPath(fillPath, {
          x: pathX,
          y: pathY,
          scale: scale,
          color: rgb(0.9, 0.9, 0.9), // Light gray fill
          borderWidth: 0,
          borderColor: undefined,
        });
      }
    }
  }

  // Draw SVG path as stroke only (no fill) on top
  page.drawSvgPath(svgPath, {
    x: pathX,
    y: pathY,
    scale: scale,
    color: undefined, // No fill
    borderWidth: 0.5,
    borderColor: rgb(0, 0, 0),
  });
}
